import type {
  ReadingKind,
  ReadingsPage,
  Thresholds,
} from '@/features/home-readings/types';
import { readingValue } from '@/features/home-readings/format';
import styles from './HomeReadings.module.css';

const W = 640;
const H = 200;
const PAD = { top: 12, right: 16, bottom: 24, left: 40 };

type Row = ReadingsPage['readings'][number];

/**
 * Home readings over time (RL-1141): one line per measure (systolic and
 * diastolic, or glucose), the API's chart band shaded and the alert
 * thresholds dashed. Out-of-range readings get a ring. Every point has a
 * tooltip; the table below is the accessible view of the same data.
 */
export function ReadingsChart({
  kind,
  rows,
  band,
  thresholds,
}: {
  kind: ReadingKind;
  rows: Row[];
  band: { low: number; high: number };
  thresholds: Thresholds;
}) {
  const points = [...rows]
    .filter((r) =>
      kind === 'blood_pressure' ? r.systolic !== null : r.glucoseMmol !== null,
    )
    .sort((a, b) => a.measuredAt.localeCompare(b.measuredAt));
  if (points.length < 2) return null;
  const series =
    kind === 'blood_pressure'
      ? [
          {
            key: 'sys',
            label: 'Systolic',
            css: styles.lineA,
            v: (r: Row) => r.systolic,
          },
          {
            key: 'dia',
            label: 'Diastolic',
            css: styles.lineB,
            v: (r: Row) => r.diastolic,
          },
        ]
      : [
          {
            key: 'glu',
            label: 'Glucose',
            css: styles.lineA,
            v: (r: Row) => r.glucoseMmol,
          },
        ];
  const limits =
    kind === 'blood_pressure'
      ? [thresholds.bpSystolicHigh, thresholds.bpDiastolicHigh]
      : [thresholds.glucoseLow, thresholds.glucoseHigh];
  const values = points.flatMap((p) =>
    series.map((s) => s.v(p)).filter((v): v is number => v !== null),
  );
  const lo = Math.min(...values, band.low, ...limits);
  const hi = Math.max(...values, band.high, ...limits);
  const span = hi - lo || 1;
  const yMin = lo - span * 0.08;
  const yMax = hi + span * 0.08;
  const t0 = new Date(points[0].measuredAt).getTime();
  const t1 = new Date(points[points.length - 1].measuredAt).getTime();
  const x = (iso: string) =>
    PAD.left +
    ((new Date(iso).getTime() - t0) / (t1 - t0 || 1)) *
      (W - PAD.left - PAD.right);
  const y = (v: number) =>
    PAD.top + (1 - (v - yMin) / (yMax - yMin)) * (H - PAD.top - PAD.bottom);
  const unit = kind === 'blood_pressure' ? 'mmHg' : 'mmol/L';
  const day = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });

  return (
    <figure className={styles.figure}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={styles.chart}
        role="img"
        aria-label={`${series.map((s) => s.label).join(' and ')} readings from ${day(points[0].measuredAt)} to ${day(points[points.length - 1].measuredAt)}, ${unit}. Shaded: chart band ${band.low} to ${band.high}. Dashed: alert thresholds. The table below lists every reading.`}
      >
        <rect
          className={styles.band}
          x={PAD.left}
          width={W - PAD.left - PAD.right}
          y={y(band.high)}
          height={Math.max(0, y(band.low) - y(band.high))}
        />
        {limits.map((l, i) => (
          <g key={i}>
            <line
              className={styles.limit}
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(l)}
              y2={y(l)}
            />
            <text
              className={styles.axis}
              x={PAD.left - 6}
              y={y(l) + 4}
              textAnchor="end"
            >
              {l}
            </text>
          </g>
        ))}
        <text className={styles.axis} x={PAD.left} y={H - 6}>
          {day(points[0].measuredAt)}
        </text>
        <text
          className={styles.axis}
          x={W - PAD.right}
          y={H - 6}
          textAnchor="end"
        >
          {day(points[points.length - 1].measuredAt)}
        </text>
        {series.map((s) => {
          const pts = points.filter((p) => s.v(p) !== null);
          return (
            <g key={s.key} className={s.css}>
              <polyline
                fill="none"
                points={pts
                  .map((p) => `${x(p.measuredAt)},${y(s.v(p)!)}`)
                  .join(' ')}
              />
              {pts.map((p) => (
                <g key={p.id}>
                  {p.outOfRange && (
                    <circle
                      className={styles.ring}
                      cx={x(p.measuredAt)}
                      cy={y(s.v(p)!)}
                      r={7}
                    />
                  )}
                  <circle cx={x(p.measuredAt)} cy={y(s.v(p)!)} r={4}>
                    <title>{`${day(p.measuredAt)}: ${readingValue(p)}`}</title>
                  </circle>
                </g>
              ))}
            </g>
          );
        })}
      </svg>
      <figcaption className={styles.legend}>
        {series.map((s) => (
          <span key={s.key} className={s.css}>
            <i aria-hidden="true" /> {s.label}
          </span>
        ))}
        <span>
          <i aria-hidden="true" className={styles.bandKey} /> Chart band{' '}
          {band.low}–{band.high}
        </span>
        <span>
          <i aria-hidden="true" className={styles.ringKey} /> Ringed: out of
          range
        </span>
      </figcaption>
    </figure>
  );
}
